import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Button, Grid, Stack, Typography,
} from '@mui/material';
import { saveSiteConfiguration } from '../../../../features';
import { toolTipText } from '../../../../utils/toolTipText';
import useToggle from '../../../../hooks/useToggle';
import { openSnackbar } from '../../../../features/SnackBar/snackBarSlice';
import { SelectBox } from '../../../../components/FormComponents';
import useStyles from '../../../../hooks/useStyles';
import styles from '../../../../layouts/pages/style';
import { generateMenuItems } from '../HighRiskAccountsConfig/constants';
import { InfoChip } from '../../../../components/CustomChip';

function KPIScoreTrendsConfig() {
  const dispatch = useDispatch();
  const siteConfiguration = useSelector((state) => state.siteConfiguration?.data);

  const [timeFrame, setTimeFrame] = useState('monthly');
  const [threshold, setThreshold] = useState(5);
  const [averageMonths, setAverageMonths] = useState(3);
  const [enableSave, setEnableSave] = useToggle();

  useEffect(() => {
    if (siteConfiguration?.scoreTrendTimeframe) {
      setTimeFrame(siteConfiguration.scoreTrendTimeframe);
    }
    if (siteConfiguration?.scoreTrendThreshold) {
      setThreshold(siteConfiguration.scoreTrendThreshold);
    }
    if (siteConfiguration?.scoreTrendMovingAvgWindow) {
      setAverageMonths(siteConfiguration.scoreTrendMovingAvgWindow);
    }
  }, [siteConfiguration]);

  const handleSubmit = () => {
    const data = {
      scoreTrendTimeframe: timeFrame,
      scoreTrendThreshold: parseInt(threshold, 10),
      scoreTrendMovingAvgWindow: parseInt(averageMonths, 10),
    };

    dispatch(saveSiteConfiguration(data))
      .then(() => {
        dispatch(
          openSnackbar({
            open: true,
            message: 'Successfully Updated !',
            type: 'success',
          }),
        );
      });

    setEnableSave(false);
  };

  const handleSaveEnable = () => {
    if (!enableSave) setEnableSave(true);
  };

  const handleChangeTimeFrame = (e) => {
    setTimeFrame(e.target.value);
    handleSaveEnable();
  };

  const handleChangeThreshold = (e) => {
    setThreshold(e.target.value);
    handleSaveEnable();
  };

  const handleChangeMovingAvg = (e) => {
    setAverageMonths(e.target.value);
    handleSaveEnable();
  };

  const classes = useStyles(styles)();

  return (
    <Box className={classes.cardStyles} sx={{ width: '100%' }}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography sx={{ fontSize: '22px', fontWeight: 500 }}>
          KPI Score Trends
        </Typography>
        {enableSave && <Button size="small" variant="outlined" onClick={handleSubmit}>Save</Button>}
      </Stack>

      {/* Content */}
      <Grid container gap={2}>
        <InfoChip icon label="KPI Score Trends are helpful in determining the performance of accounts" />

        <Grid item container display="flex" justifyContent="space-between" alignItems="flex-end">
          <Grid item xs={8}>
            <Typography>Time Frame</Typography>
            <Typography fontSize="small">{toolTipText.watchList.VIEW}</Typography>
          </Grid>

          <Grid item xs={3}>
            <SelectBox
              name="view"
              title="Time Frame"
              data={[
                { id: 'monthly', name: 'Monthly', value: 'monthly' },
                { id: 'weekly', name: 'Weekly', value: 'weekly' },
              ]}
              value={timeFrame}
              onChange={handleChangeTimeFrame}
            />

          </Grid>
        </Grid>

        <Grid item container display="flex" justifyContent="space-between" alignItems="flex-end">
          <Grid item xs={8}>
            <Typography>Threshold</Typography>
            <Typography fontSize="small">{toolTipText.watchList.THRESHOLD_INFO}</Typography>
          </Grid>

          <Grid item xs={3}>
            <SelectBox
              name="threshold"
              title="Threshold"
              data={generateMenuItems(5, 30)}
              value={threshold}
              onChange={handleChangeThreshold}
            />
          </Grid>
        </Grid>

        <Grid item container display="flex" justifyContent="space-between" alignItems="flex-end">
          <Grid item xs={8}>
            <Typography>Moving Average Window</Typography>
            <Typography fontSize="small">{toolTipText.watchList.MOVING_AVERAGE}</Typography>
          </Grid>

          <Grid item xs={3}>
            <SelectBox
              name="averageMonths"
              title="Moving Average Window"
              data={generateMenuItems(2, 3)}
              value={averageMonths}
              onChange={handleChangeMovingAvg}
            />
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
}

export default KPIScoreTrendsConfig;
